// New / edit container form (Stage 6 plan §D "containerDialog.vue"). Pure: no Vue, no API calls.
// The API checks every rule again; these checks only save a round trip.

export const CONTAINER_TYPES = [
  "drum",
  "tank",
  "bin",
  "big_bag",
  "pallet",
  "area",
];
export const CONTAINER_UNITS = ["l", "kg"];
export const NAME_MAX = 80;
export const NOTE_MAX = 500;
export const TARE_MAX = 60000;

export function emptyForm() {
  return {
    name: "",
    type: "drum",
    unit: "l",
    tare: "",
    note: "",
    wasteCodeId: null,
  };
}

export function formFromContainer(container) {
  return {
    name: container.name || "",
    type: container.type,
    unit: container.unit,
    tare: container.tare == null ? "" : String(container.tare),
    note: container.note || "",
    wasteCodeId: (container.waste && container.waste.codeId) || null,
  };
}

// An area holds car bodies and is weighed in kg.
export function unitsForType(type) {
  return type === "area" ? ["kg"] : CONTAINER_UNITS;
}

// "18,5" and "18.5" are 18.5; empty is null; anything else NaN.
export function parseTare(value) {
  if (value === null || value === undefined) return null;
  const text = String(value).trim().replace(",", ".");
  if (!text) return null;
  return /^\d+(\.\d+)?$/.test(text) ? Number(text) : NaN;
}

// Select items, ordered by code: "13 02 05* · scarti di olio minerale ...".
export function codeItems(codes = []) {
  return codes.map((code) => ({
    value: code.id,
    title: `${code.code} · ${code.description}`,
    code: code.code,
    hazardous: code.hazardous === true,
    verified: code.verified !== false,
  }));
}

// The hint under the code select: the tasks the code is suggested for, and the other code of a
// mirror pair (the manager picks one, e.g. 16 01 14* / 16 01 15). Null without a code.
export function codeHint(codes = [], wasteCodeId) {
  const code = codes.find((c) => c.id === wasteCodeId);
  if (!code) return null;
  const mirror = code.mirrorOf
    ? codes.find((c) => c.code === code.mirrorOf) || null
    : null;
  return {
    suggestedTasks: [...(code.suggestedTasks || [])],
    mirror: mirror ? { id: mirror.id, code: mirror.code } : null,
    verified: code.verified !== false,
  };
}

// { field: i18n key } of the first problem of each field; empty when the form can be sent.
export function validateForm(form) {
  const errors = {};
  const name = (form.name || "").trim();
  if (!name) errors.name = "waste.form.errors.name_required";
  else if (name.length > NAME_MAX) errors.name = "waste.form.errors.name_long";
  if (!CONTAINER_TYPES.includes(form.type))
    errors.type = "waste.form.errors.required";
  if (!unitsForType(form.type).includes(form.unit)) {
    errors.unit =
      form.type === "area"
        ? "waste.form.errors.area_kg"
        : "waste.form.errors.required";
  }
  const tare = parseTare(form.tare);
  if (tare !== null) {
    if (Number.isNaN(tare) || tare <= 0 || tare > TARE_MAX) {
      errors.tare = "waste.form.errors.tare_invalid";
    } else if (Math.abs(tare * 10 - Math.round(tare * 10)) > 1e-9) {
      errors.tare = "waste.form.errors.tare_decimals";
    }
  }
  if ((form.note || "").length > NOTE_MAX)
    errors.note = "waste.form.errors.note_long";
  if (!form.wasteCodeId) errors.wasteCodeId = "waste.form.errors.code_required";
  return errors;
}

function normalized(form) {
  const note = (form.note || "").trim();
  return {
    name: (form.name || "").trim().replace(/\s+/g, " "),
    type: form.type,
    unit: form.unit,
    tare: parseTare(form.tare),
    note: note || null,
    wasteCodeId: form.wasteCodeId,
  };
}

export function createPayload(form) {
  return normalized(form);
}

// Only the changed fields. A used container never sends its code or unit (they are fixed).
export function changedSet(form, container, { codeLocked = false } = {}) {
  const next = normalized(form);
  const before = normalized(formFromContainer(container));
  const set = {};
  Object.keys(next).forEach((key) => {
    if (codeLocked && (key === "wasteCodeId" || key === "unit")) return;
    if (next[key] !== before[key]) set[key] = next[key];
  });
  return set;
}

// A server refusal as { field, key } for the form, or null when it belongs in the alert.
export function serverError(err) {
  const data = (err && err.response && err.response.data) || {};
  const field = data.details && data.details.field;
  switch (data.code) {
    case "name_taken":
      return { field: "name", key: "waste.form.errors.name_taken" };
    case "unknown_code":
      return { field: "wasteCodeId", key: "waste.form.errors.unknown_code" };
    case "code_locked":
      return {
        field: field === "unit" ? "unit" : "wasteCodeId",
        key: "waste.form.errors.code_locked",
      };
    case "revision_conflict":
      return { field: null, key: "waste.form.errors.conflict" };
    case "not_manager":
      return { field: null, key: "waste.form.errors.not_manager" };
    case "no_change":
      return { field: null, key: "waste.form.errors.no_change" };
    case "invalid_input":
      return field
        ? { field, key: "waste.form.errors.invalid" }
        : { field: null, key: "waste.form.errors.invalid" };
    default:
      return null;
  }
}
